import React, { useEffect, useState } from 'react';
import axios from 'axios';

const Siteinfo = () => {
  const api = process.env.REACT_APP_API_URL as string;
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [socials, setSocials] = useState<{ icon: File | null; iconUrl?: string; link: string }[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [numbers, setNumbers] = useState<string[]>([]);

  // 🔽 Fetch data on component mount
  useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await axios.get(`${api}/auth/siteinfo`);
      const data = res.data;

      // Logo preview
      const logoUrl = data.logo ? `${api}/${data.logo}` : null;
      setLogoPreview(logoUrl);

      // ✅ Convert logo URL to File object and set it
      if (logoUrl) {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const file = new File([blob], 'logo.jpg', { type: blob.type });
        setLogo(file);
      }

      // Socials
      const parsedSocials = JSON.parse(data.social || '[]');
      setSocials(parsedSocials.map((s: any) => ({
        icon: null,
        iconUrl: `${api}/${s.icon}`,
        link: s.link
      })));

      // Emails and Numbers
      setEmails((data.emails || '').split(','));
      setNumbers((data.number || '').split(','));
    } catch (err) {
      console.error('Failed to fetch site info', err);
    }
  };

  fetchData();
}, [api]);


  const handleAddField = (type: 'social' | 'email' | 'number') => {
    if (type === 'social') setSocials([...socials, { icon: null, link: '' }]);
    if (type === 'email') setEmails([...emails, '']);
    if (type === 'number') setNumbers([...numbers, '']);
  };

  const handleRemoveField = (type: 'social' | 'email' | 'number', index: number) => {
    if (type === 'social') setSocials(socials.filter((_, i) => i !== index));
    if (type === 'email') setEmails(emails.filter((_, i) => i !== index));
    if (type === 'number') setNumbers(numbers.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    console.log('imagePath:', logo);
console.log('socialLinks:', socials);
console.log('email:', emails);
console.log('number:', numbers);

    if (!logo || socials.length === 0 || emails.length === 0 || numbers.length === 0) {
       alert('Missing required fields');
  return ;
}

    const formData = new FormData();

    if (!logo && !logoPreview) return alert('Logo is required.');
    if (logo) formData.append('image', logo);

    socials.forEach((s) => {
      if (s.icon) formData.append('socialIcons', s.icon);
      formData.append('socialLinks', s.link);
    });

    emails.forEach(e => formData.append('email', e));
    numbers.forEach(n => formData.append('number', n));

    try {
      const res = await axios.put(`${api}/auth/siteinfo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(res.data.message);
    } catch (err) {
      console.error(err);
      alert('Error submitting site info');
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white rounded-xl shadow-lg space-y-8">
      <h1 className="text-2xl font-bold text-gray-800">Site Info Form</h1>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Logo</label>
        {logoPreview && (
          <img src={logoPreview} alt="Logo" className="h-16 mb-2" />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0] || null;
            setLogo(file);
            setLogoPreview(file ? URL.createObjectURL(file) : null);
          }}
          className="block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
        />
      </div>

      {/* Social Media */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Social Media Links</h2>
        <div className="space-y-4">
          {socials.map((s, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
              {s.iconUrl && !s.icon && (
                <img src={s.iconUrl} alt="Icon" className="h-10 w-10 object-contain" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={e => {
                  const updated = [...socials];
                  updated[i].icon = e.target.files?.[0] || null;
                  delete updated[i].iconUrl;
                  setSocials(updated);
                }}
                className="block w-full text-sm"
              />
              <input
                type="text"
                placeholder="https://..."
                value={s.link}
                onChange={e => {
                  const updated = [...socials];
                  updated[i].link = e.target.value;
                  setSocials(updated);
                }}
                className="border border-gray-300 p-2 rounded-md w-full shadow-sm"
              />
              <button
                onClick={() => handleRemoveField('social', i)}
                className="text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => handleAddField('social')}
          className="mt-3 inline-flex items-center text-sm text-blue-600 hover:underline"
        >
          + Add Social
        </button>
      </div>

      {/* Emails */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Email Addresses</h2>
        {emails.map((email, i) => (
          <div key={i} className="flex gap-3 items-center">
            <input
              type="email"
              value={email}
              onChange={e => {
                const updated = [...emails];
                updated[i] = e.target.value;
                setEmails(updated);
              }}
              className="border border-gray-300 p-2 rounded-md w-full shadow-sm"
            />
            <button onClick={() => handleRemoveField('email', i)} className="text-red-600 hover:underline">Remove</button>
          </div>
        ))}
        <button
          onClick={() => handleAddField('email')}
          className="mt-3 inline-flex items-center text-sm text-blue-600 hover:underline"
        >
          + Add Email
        </button>
      </div>

      {/* Numbers */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Phone Numbers</h2>
        {numbers.map((num, i) => (
          <div key={i} className="flex gap-3 items-center">
            <input
              type="text"
              value={num}
              onChange={e => {
                const updated = [...numbers];
                updated[i] = e.target.value;
                setNumbers(updated);
              }}
              className="border border-gray-300 p-2 rounded-md w-full shadow-sm"
            />
            <button onClick={() => handleRemoveField('number', i)} className="text-red-600 hover:underline">Remove</button>
          </div>
        ))}
        <button
          onClick={() => handleAddField('number')}
          className="mt-3 inline-flex items-center text-sm text-blue-600 hover:underline"
        >
          + Add Number
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition"
        >
          Save Site Info
        </button>
      </div>
    </div>
  );
};

export default Siteinfo;
